import { useEffect } from 'react';
import clsx from 'clsx';
import { Container } from './Container';
import { AppLink } from './AppLink';
import type { Photo } from '@/lib/types';

interface Props {
  title: string;
  intro: string;
  image?: Photo;
  /**
   * 'slim' (about 260px) keeps the page's content on the first screen; 'tall' (21:8) shows a
   * photograph more fully, for the faculty close-ups that a thin strip crops badly.
   */
  bannerSize?: 'slim' | 'tall';
  objectPosition?: string;
  /** A way back to the parent page, only where visitors need one (a faculty page). */
  back?: { label: string; to: string };
}

// Title band shared by every page except Home. The browser tab already carries
// the page name, so there is no breadcrumb trail.
export function PageHeader({ title, intro, image, bannerSize = 'slim', objectPosition = 'center', back }: Props) {
  useEffect(() => {
    document.title = `${title} · Indian Technical Education Society – Mumbai`;
  }, [title]);

  return (
    <section aria-labelledby="page-title" className="border-b border-rule bg-paper">
      <Container className="pt-[clamp(32px,5vw,64px)] pb-[clamp(28px,4vw,48px)]">
        {back && (
          <AppLink
            href={back.to}
            className="tap mb-4 inline-block font-mono text-[0.75rem] tracking-[0.06em] text-teal-700 uppercase no-underline"
          >
            <span aria-hidden="true">← </span>
            {back.label}
          </AppLink>
        )}
        <h1
          id="page-title"
          tabIndex={-1}
          className="m-0 max-w-[20em] font-serif text-h1 font-semibold text-balance text-navy-900 outline-none"
        >
          {title}
        </h1>
        <p className="mt-[18px] max-w-[40em] text-intro text-pretty text-ink-2">{intro}</p>
      </Container>
      {image && (
        <Container className="pb-[clamp(32px,4vw,48px)]">
          <img
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            style={{ objectPosition }}
            className={clsx(
              'block w-full bg-img-fallback object-cover',
              bannerSize === 'tall' ? 'aspect-[21/8]' : 'h-[clamp(140px,18vw,260px)]',
            )}
          />
        </Container>
      )}
    </section>
  );
}
